import React, { useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css"; // Import your CSS file
import Header from "./components/Header";
import Footer from "./components/Footer";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import AuthGuard from "./components/AuthGuard";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import GroupPage from "./pages/GroupsPage";
import PlaysPage from "./pages/PlaysPage";
import GroupDetailPage from "./pages/GroupDetailPage";
import PlayDetailPage from "./pages/PlayDetailPage";
import PlayDetailPageWrapper from "./pages/PlayDetailPageWrapper";
import ProgramDetailPage from "./pages/ProgramDetailPage";
import BookingPage from "./pages/BookingPage";
import BookingHistoryPage from "./pages/BookingHistoryPage";
import BookingVerifyPage from "./pages/BookingVerifyPage";
import BookingVerificationPage from "./pages/BookingVerificationPage";
import ErrorPage from "./pages/ErrorPage";
import SigningPage from "./pages/SigningPage";
import ManageGroupPage from "./pages/ManageGroupPage";
import ManageBookingPage from "./pages/ManageBookingPage";
import ManagePlayPage from "./pages/ManagePlayPage";
import ManageProgramPage from "./pages/ManageProgramPage";
import ManageSchedulePage from "./pages/ManageSchedulePage";
import ManageTicketPage from "./pages/ManageTicketPage";
import ProfilePage from "./pages/ProfilePage";
// import SchedulePage from "./pages/SchedulePage";
import ForgetPasswordPage from "./pages/ForgetPasswordPage";

import ManageAllBookingsPage from "./pages/ManageAllBookingsPage";
import ManageAllGroupsPage from "./pages/ManageAllGroupsPage";
import ManageAllPlaysPage from "./pages/ManageAllPlaysPage";
import ManageAllProgramsPage from "./pages/ManageAllProgramsPage";
import ManageAllUsersPage from "./pages/ManageAllUsersPage";
import ManageAllContentPage from "./pages/ManageAllContentPage";
import ManagePlaySchedulesPage from "./pages/ManagePlaySchedulesPage";
import ManageYoutubeVideosPage from "./pages/ManageYoutubeVideosPage";
import ManageSiteConfigPage from "./pages/ManageSiteConfigPage";

import ManageTicketsPage from "./pages/ManageTicketsPage";
import PackageSelectionPage from "./pages/PackageSelectionPage";
import PaymentPage from "./pages/PaymentPage";
import GroupPackagePage from "./pages/GroupPackagePage";
import ManagePackagePage from "./pages/ManagePackagePage";
import PackageStatsPage from "./pages/PackageStatsPage";
import PackageAssignmentPage from "./pages/PackageAssignmentPage";
import ArticlesPage from "./pages/ArticlesPage";
import NewsPage from "./pages/NewsPage";
import ReviewsPage from "./pages/ReviewsPage";
import TutorialsPage from "./pages/TutorialsPage";
import VideosPage from "./pages/VideosPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import ProgramSchedulePage from "./pages/ProgramSchedulePage";
import SchedulesPage from "./pages/SchedulesPage";
import DashboardPage from "./pages/DashboardPage";
import BookingDetailsPage from "./pages/BookingDetailsPage";
import { useDispatch, useSelector } from "react-redux";
import { login, menuPermission } from "./states/slices/authSlice";
import getCookie from "./services/GlobalService";
import NotificationHandler from "./components/NotificationHandler";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { requestNotificationPermission, onMessageListener } from './services/NotificationService';
function App() {
  const dispatch = useDispatch();
  
  useEffect(() => {
    // Initialize authentication state from storage
    const jwtTokenFromStorage = localStorage.getItem("jwtToken");
    const menuPermissionList = getCookie("menuPermissions");
    
    if (jwtTokenFromStorage) {
      try {
        const parsedToken = JSON.parse(jwtTokenFromStorage);
        dispatch(login(parsedToken));
        if (menuPermissionList) {
          dispatch(
            menuPermission({ menuPermissions: menuPermissionList.split(",") })
          );
        }
        
        // Request notification permission for logged-in users
        requestNotificationPermission();
      } catch (error) {
        console.error("Error parsing JWT token from storage:", error);
        localStorage.removeItem("jwtToken");
      }
    }
    
    // Listen for foreground messages
    onMessageListener().then((payload: any) => {
      if (payload?.notification) {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: '/favicon.ico'
        });
      }
    }).catch(err => console.log('Failed to listen for messages:', err));
  }, [dispatch]);
  return (
    <HelmetProvider>
      <Router>
        <div className="d-flex flex-column" style={{ minHeight: "100vh" }}>
          <Header />
          <div className="container mt-4 content flex-grow-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/groups" element={<GroupPage />} />
              <Route path="/plays" element={<PlaysPage />} />
              <Route path="/group/:id" element={<GroupDetailPage />} />
              <Route path="/g/:groupCode" element={<GroupDetailPage />} />
              <Route path="/play/:id" element={<PlayDetailPage />} />
              <Route path="/p/:playcode" element={<PlayDetailPageWrapper />} />
              <Route path="/program/:id" element={<ProgramDetailPage />} />
              <Route path="/booking" element={<AuthGuard><BookingPage /></AuthGuard>} />
              <Route path="/booking-history" element={<AuthGuard><BookingHistoryPage /></AuthGuard>} />
              <Route path="/booking-verify" element={<AuthGuard><BookingVerifyPage /></AuthGuard>} />
              <Route path="/verify-booking" element={<BookingVerificationPage />} />
              <Route path="/signing" element={<SigningPage />} />
              <Route path="/profile" element={<AuthGuard><ProfilePage /></AuthGuard>} />
              <Route path="/forgot-password" element={<ForgetPasswordPage />} />
              <Route path="/dashboard" element={<AuthGuard><DashboardPage /></AuthGuard>}>
                <Route path="manage-group" element={<ManageGroupPage />} />
                <Route path="manage-play" element={<ManagePlayPage />} />
                <Route path="manage-bookings" element={<ManageBookingPage />} />
                <Route path="manage-program" element={<ManageProgramPage />} />
                <Route path="manage-schedule" element={<ManageSchedulePage />} />
                <Route path="manage-ticket" element={<ManageTicketPage />} />
                <Route path="booking-history" element={<BookingHistoryPage />} />
                <Route path="manage-all-bookings" element={<ManageAllBookingsPage />} />
                <Route path="manage-all-groups" element={<ManageAllGroupsPage />} />
                <Route path="manage-all-plays" element={<ManageAllPlaysPage />} />
                <Route path="manage-all-programs" element={<ManageAllProgramsPage />} />
                <Route path="manage-play-schedules" element={<ManagePlaySchedulesPage />} />
                <Route path="manage-all-content" element={<ManageAllContentPage />} />
                <Route path="manage-youtube-videos" element={<ManageYoutubeVideosPage />} />
                <Route path="site-config" element={<ManageSiteConfigPage />} />

                <Route path="manage-all-users" element={<ManageAllUsersPage />} />
                <Route path="manage-all-tickets" element={<ManageTicketsPage />} />
                <Route path="package-selection" element={<PackageSelectionPage />} />
                <Route path="payment" element={<PaymentPage />} />
                <Route path="group-package" element={<GroupPackagePage />} />
                <Route path="manage-package" element={<ManagePackagePage />} />
                <Route path="package-stats" element={<PackageStatsPage />} />
                <Route path="package-assignment" element={<PackageAssignmentPage />} />
              </Route>
              {/* Legacy routes for backward compatibility */}
              <Route path="/manage-group" element={<AuthGuard><ManageGroupPage /></AuthGuard>} />
              <Route path="/manage-play" element={<AuthGuard><ManagePlayPage /></AuthGuard>} />
              <Route path="/manage-bookings" element={<AuthGuard><ManageBookingPage /></AuthGuard>} />
              <Route path="/manage-program" element={<AuthGuard><ManageProgramPage /></AuthGuard>} />
              <Route path="/manage-schedule" element={<AuthGuard><ManageSchedulePage /></AuthGuard>} />
              <Route path="/manage-ticket" element={<AuthGuard><ManageTicketPage /></AuthGuard>} />
              <Route path="/manage-all-bookings" element={<AuthGuard><ManageAllBookingsPage /></AuthGuard>} />
              <Route path="/manage-all-groups" element={<AuthGuard><ManageAllGroupsPage /></AuthGuard>} />
              <Route path="/manage-all-plays" element={<AuthGuard><ManageAllPlaysPage /></AuthGuard>} />
              <Route path="/manage-all-programs" element={<AuthGuard><ManageAllProgramsPage /></AuthGuard>} />
              <Route path="/manage-all-users" element={<AuthGuard><ManageAllUsersPage /></AuthGuard>} />
              <Route path="/articles" element={<ArticlesPage />} />
              <Route path="/article/:id" element={<ArticleDetailPage />} />
              <Route path="/play/:id/schedule" element={<ProgramSchedulePage />} />
              <Route path="/program/:id/schedule" element={<ProgramSchedulePage />} />
              <Route path="/schedules" element={<SchedulesPage />} />
              <Route path="/booking-details/:bookingId" element={<BookingDetailsPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/tutorials" element={<TutorialsPage />} />
              <Route path="/videos" element={<VideosPage />} />
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </div>
          <Footer />
        </div>
        <NotificationHandler />
        <ToastContainer />
      </Router>
    </HelmetProvider>
  );
}

export default App;
